import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Incentive } from 'src/entities/incentives';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Button,
	Flex,
	useToast,
} from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { useDeactivateIncentive } from '../../../queries/incentives';
import { AdminIncentiveTrackingEvent } from './events';

type DeactivateIncentiveModalProps = {
	incentive: Incentive;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	redirectPath?: string;
};

export const DeactivateIncentiveModal: FC<DeactivateIncentiveModalProps> = ({
	incentive,
	isOpen,
	onClose,
	onSuccess,
	redirectPath = '/admin/programs/incentives',
}) => {
	const toast = useToast();
	const navigate = useNavigate();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const { t } = useTranslation('incentives', { keyPrefix: 'deactivate' });
	const { track } = useAnalytics();

	const { mutate, isLoading } = useDeactivateIncentive(incentive, {
		onMutate: () => {
			track(AdminIncentiveTrackingEvent.DeactivateIncentive, { incentiveId: incentive.id });
		},
		onSuccess: () => {
			onClose();
			toast({
				title: t('success', { name: incentive.name }),
				status: 'success',
			});

			if (onSuccess) {
				onSuccess();
			} else if (redirectPath) {
				navigate(redirectPath);
			}
		},
		onError: () => {
			toast({
				title: t('error'),
				status: 'error',
			});
		},
	});

	return (
		<AlertDialog leastDestructiveRef={cancelRef} isOpen={isOpen} onClose={onClose} size="lg">
			<AlertDialogOverlay>
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						{t('title')}
					</AlertDialogHeader>
					<AlertDialogCloseButton />
					<AlertDialogBody fontWeight="semibold">{t('confirmationMessage', { incentiveName: incentive.name })}</AlertDialogBody>

					<AlertDialogBody>{t('deactivateTimeMessage')}</AlertDialogBody>

					<AlertDialogFooter>
						<Flex justify="space-between" w="full">
							<Button ref={cancelRef} onClick={onClose} variant="alpha" colorScheme="secondary">
								{t('cancelButtonLabel')}
							</Button>
							<Button colorScheme="primary" variant="solid" isLoading={isLoading} onClick={() => mutate()} ml={3}>
								{t('deactivateButtonLabel')}
							</Button>
						</Flex>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
	);
};
